import { createBrowserRouter, Navigate } from 'react-router-dom';
import { lazy } from 'react';
import RootLayout from './RootLayout';
import SuspenseComponent from '@/shared/components/Generic/SuspenseComponent/SuspenseComponent';
import DashboardLayout from '@/layouts/DashboardLayout';
import AuthLayout from '@/layouts/AuthLayout';
import { ProtectedRoute } from '@/modules/auth/components/ProtectedRoute';
import TierGateProvider from '@/shared/context/TierGateProvider';

const LogIn = lazy(() => import('@/modules/auth/pages/Login'));
const ForgotPassword = lazy(() => import('@/modules/auth/pages/ForgotPassword'));
const OAuthCallback = lazy(() => import('@/modules/auth/pages/OAuthCallback'));
const B2BRegistration = lazy(() => import('@/modules/auth/pages/B2BRegistration'));
const B2BRegistrationConfirmation = lazy(
  () => import('@/modules/auth/pages/B2BRegistrationConfirmation')
);
const ResetPassword = lazy(() => import('@/modules/auth/pages/ResetPassword'));
const ActivateAccount = lazy(() => import('@/modules/auth/pages/ActivateAccount'));

const Dashboard = lazy(() => import('@/modules/dashboard/pages/Dashboard'));
const ArticleManagement = lazy(() => import('@/modules/articles/pages/ArticleManagement'));
const ArticleEditor = lazy(() => import('@/modules/articles/pages/ArticleEditor'));
const BrandManagement = lazy(() => import('@/modules/brands/pages/BrandManagement'));
const BrandDetails = lazy(() => import('@/modules/brands/pages/BrandDetails'));
const Profile = lazy(() => import('@/modules/profile/pages/Profile'));
const SupplierProfile = lazy(() => import('@/modules/profile/pages/SupplierProfile'));
const Settings = lazy(() => import('@/modules/settings/pages/Settings'));
const Subscription = lazy(() => import('@/modules/subscription/pages/Subscription'));
const SubscriptionSuccess = lazy(() => import('@/modules/subscription/pages/SubscriptionSuccess'));
const SubscriptionCancel = lazy(() => import('@/modules/subscription/pages/SubscriptionCancel'));
const AdvertorialManagement = lazy(
  () => import('@/modules/advertorials/pages/AdvertorialManagement')
);
const AdvertorialDetails = lazy(() => import('@/modules/advertorials/pages/AdvertorialDetails'));
const LeadManagement = lazy(() => import('@/modules/leads/pages/LeadManagement'));
const ReportManagement = lazy(() => import('@/modules/reports/pages/ReportManagement'));

export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      {
        path: 'auth',
        element: (
          <SuspenseComponent>
            <AuthLayout />
          </SuspenseComponent>
        ),
        children: [
          { index: true, element: <Navigate to='/auth/login' replace /> },
          { path: 'login', element: <LogIn /> },
          { path: 'registration', element: <B2BRegistration /> },
          {
            path: 'registration/confirmation/:status',
            element: <B2BRegistrationConfirmation />,
          },
          { path: 'forgot-password', element: <ForgotPassword /> },
          { path: 'oauth-callback', element: <OAuthCallback /> },
          { path: 'reset-password/:token', element: <ResetPassword /> },
          { path: 'activate/:token', element: <ActivateAccount /> },
        ],
      },
      {
        path: '*',
        element: (
          <SuspenseComponent>
            <ProtectedRoute>
              <TierGateProvider>
                <DashboardLayout />
              </TierGateProvider>
            </ProtectedRoute>
          </SuspenseComponent>
        ),
        children: [
          { path: 'dashboard', element: <Dashboard /> },
          { path: 'articles', element: <ArticleManagement /> },
          { path: 'articles/new', element: <ArticleEditor /> },
          { path: 'articles/:articleId/edit', element: <ArticleEditor /> },
          { path: 'brands', element: <BrandManagement /> },
          { path: 'brands/new', element: <BrandDetails /> },
          { path: 'brands/:brandId/edit', element: <BrandDetails /> },
          { path: 'profile', element: <Profile /> },
          { path: 'supplier-profile', element: <SupplierProfile /> },
          { path: 'settings', element: <Settings /> },
          { path: 'subscription', element: <Subscription /> },
          { path: 'subscription/success', element: <SubscriptionSuccess /> },
          { path: 'subscription/cancel', element: <SubscriptionCancel /> },
          { path: 'leads', element: <LeadManagement /> },
          { path: 'advertorials', element: <AdvertorialManagement /> },
          { path: 'advertorials/new', element: <AdvertorialDetails /> },
          { path: 'advertorials/:advertorialId/edit', element: <AdvertorialDetails /> },
          { path: 'reports', element: <ReportManagement /> },
        ],
      },
    ],
  },
]);
